import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";

export const ContactForm = () => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error(t("common.required_field"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", { name, email, phone, message });
      toast.success(t("contact.success"));
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-zinc-500 mb-4">{t("contact.label")}</p>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tighter mb-6">
              {t("contact.title_a")}
              <em className="italic text-zinc-400">{t("contact.title_em")}</em>
              {t("contact.title_b")}
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-10 max-w-md">{t("contact.intro")}</p>

            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-4 text-zinc-300">
                <Mail size={16} strokeWidth={1.5} className="text-zinc-500" />
                contact@versantstudio.fr
              </li>
              <li className="flex items-center gap-4 text-zinc-300">
                <Phone size={16} strokeWidth={1.5} className="text-zinc-500" />
                <a href="tel:+33644051995" className="hover:text-white">+33 6 44 05 19 95</a>
              </li>
              <li className="flex items-center gap-4 text-zinc-300">
                <MapPin size={16} strokeWidth={1.5} className="text-zinc-500" />
                {t("contact.location")}
              </li>
            </ul>

            <div className="mt-10 p-6 border border-white/10 bg-zinc-900/30">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("contact.box_t")}</p>
              <p className="text-sm text-zinc-300 leading-relaxed">{t("contact.box_d")}</p>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 bg-zinc-900/30 p-8 sm:p-10 border border-white/10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label={t("contact.f_name")}>
                <input
                  data-testid="contact-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-zinc-950 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
                />
              </Field>
              <Field label={t("contact.f_email")}>
                <input
                  data-testid="contact-email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-zinc-950 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
                />
              </Field>
            </div>
            <Field label={t("contact.f_phone")} className="mt-5">
              <input
                data-testid="contact-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-zinc-950 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
              />
            </Field>
            <Field label={t("contact.f_message")} className="mt-5">
              <textarea
                data-testid="contact-message"
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full bg-zinc-950 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white resize-y"
              />
            </Field>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="mt-6 inline-flex items-center justify-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 transition-colors px-7 py-4 text-sm font-medium disabled:opacity-50"
            >
              <Send size={14} />
              {loading ? t("contact.sending") : t("contact.cta")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2">{label}</span>
    {children}
  </label>
);
